<template>
  <div class="reports-page">
    <div
      v-if="isLoading"
      class="text-center py-3"
    >
      <b-spinner label="Loading reports..." />
    </div>
    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
    >
      {{ loadError }}
    </b-alert>
    <b-row v-else>
      <b-col
        v-for="(item, index) in reportCards"
        :key="`report-${index}`"
        lg="4"
        md="6"
        cols="12"
        class="mb-1"
      >
        <b-card no-body class="report-card" @click="goTo(item.route)">
          <div class="report-card-content">
            <div>
              <h5 class="report-title mb-0">{{ item.title }}</h5>
              <small class="report-subtitle">This month</small>
              <div class="report-value">{{ item.value }}</div>
              <small
                v-if="item.growth"
                class="report-growth"
                :class="{ 'report-growth--negative': item.growth.startsWith('-') }"
              >
                {{ item.growth }}
              </small>
            </div>
            <div class="report-donut">
              <div class="report-donut-inner">
                <strong>{{ item.total }}</strong>
                <span>Total</span>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BAlert,
  BCard,
  BCol,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { adminPanelApi } from '@/services/adminPanelApi'

const CARD_DEFS = [
  { key: 'suspended_users', title: 'Suspended Users', route: 'reports-suspended-users' },
  { key: 'suspended_advertisers', title: 'Suspended Advertisers', route: 'reports-suspended-advertisers' },
  { key: 'reported_ads', title: 'User-Reported Ads', route: 'reports-reported-by-users' },
  { key: 'ads', title: 'Ads', route: 'reports-ads' },
  { key: 'advertisers', title: 'Advertisers', route: 'reports-advertisers' },
  { key: 'users', title: 'Users', route: 'reports-users' },
  { key: 'rejected_ads', title: 'Rejected Ad', route: 'reports-reject-ads' },
]

export default {
  name: 'ReportsOverview',
  components: {
    BAlert,
    BCard,
    BCol,
    BRow,
    BSpinner,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      reportCards: CARD_DEFS.map(c => ({
        ...c,
        value: '—',
        total: '—',
        growth: '',
      })),
    }
  },
  created() {
    this.fetchMetrics()
  },
  methods: {
    goTo(routeName) {
      this.$router.push({ name: routeName })
    },
    formatGrowth(val) {
      if (val === null || val === undefined || val === '') return ''
      const n = Number(val)
      if (Number.isNaN(n)) return ''
      if (n === 0) return '0.0%'
      return `${n > 0 ? '+' : '-'}${Math.abs(n).toFixed(1)}%`
    },
    async fetchMetrics() {
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.reportsDashboard()
        const metrics = res.data?.data || res.data || {}
        this.reportCards = CARD_DEFS.map(def => {
          const m = metrics[def.key] || metrics[def.key.replace('_', '-')] || {}
          return {
            ...def,
            value: String(m.count ?? m.value ?? m.monthly_count ?? '—'),
            total: String(m.total ?? m.monthly_count ?? '—'),
            growth: this.formatGrowth(m.growth),
          }
        })
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load reports.'
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.report-card {
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
  border-radius: 4px;
  cursor: pointer;
}

.report-card-content {
  padding: 1rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.report-title {
  color: #4b4b5a;
  font-weight: 700;
  font-size: 1.35rem;
}

.report-subtitle {
  color: #8e8ea3;
  font-size: 0.95rem;
}

.report-value {
  margin-top: 0.3rem;
  color: #3c3c4b;
  font-weight: 700;
  font-size: 2.2rem;
  line-height: 1.1;
}

.report-growth {
  color: #28c76f;
  font-size: 1.05rem;
  font-weight: 700;
}

.report-growth--negative {
  color: #ea5455;
}

.report-donut {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  border: 10px solid #28c76f;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.report-donut-inner {
  text-align: center;
  color: #7d8090;
  line-height: 1;
}

.report-donut-inner strong {
  display: block;
  color: #5a5c6d;
  font-size: 1rem;
}

.report-donut-inner span {
  font-size: 0.72rem;
}
</style>
